/**
 * CSV helpers for label export/import and labelset-from-CSV flows.
 *
 * Supported columns (header row, case-insensitive):
 *   name (required), color, type, parent
 *
 * Color may be "r,g,b" or "#rrggbb". Type is 1 (box) or 2 (point).
 * Parent is the parent label name (optional).
 *
 * Also accepts a single-column file of names (with or without a "name" header).
 * Missing type defaults to 1 (bounding box), parent to null, and color to a
 * random palette color not already used by existing labels when possible.
 */

/** Same palette as api-echo DefaultLabelColorForProject / Flask DEFAULT_LABEL_COLORS. */
export const DEFAULT_LABEL_COLORS = [
  '249,65,68',
  '243,114,44',
  '248,150,30',
  '67,170,139',
  '249,132,74',
  '249,199,79',
  '144,190,109',
  '77,144,142',
  '87,117,144',
  '39,125,161',
];

const DEFAULT_TYPE = 1;

function stripBom(text) {
  if (text && text.charCodeAt(0) === 0xfeff) {
    return text.slice(1);
  }
  return text;
}

/** Minimal RFC4180-ish CSV parser (handles quotes and commas inside fields). */
export function parseCsv(text) {
  const input = stripBom(String(text || ''));
  const rows = [];
  let row = [];
  let field = '';
  let i = 0;
  let inQuotes = false;

  while (i < input.length) {
    const ch = input[i];
    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === ',') {
      row.push(field);
      field = '';
      i += 1;
      continue;
    }
    if (ch === '\n') {
      row.push(field);
      field = '';
      if (row.length > 1 || (row.length === 1 && row[0].trim() !== '')) {
        rows.push(row);
      }
      row = [];
      i += 1;
      continue;
    }
    if (ch === '\r') {
      i += 1;
      continue;
    }
    field += ch;
    i += 1;
  }
  row.push(field);
  if (row.length > 1 || (row.length === 1 && row[0].trim() !== '')) {
    rows.push(row);
  }
  return rows;
}

function escapeCsvField(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function rowsToCsv(rows) {
  return rows.map((row) => row.map(escapeCsvField).join(',')).join('\n') + '\n';
}

function normalizeHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

function normalizeColor(raw) {
  if (raw == null) {
    return null;
  }
  const value = String(raw).trim();
  if (!value) {
    return null;
  }
  if (value.startsWith('#') && value.length >= 7) {
    const hex = value.slice(1, 7);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if ([r, g, b].every((n) => Number.isFinite(n))) {
      return `${r},${g},${b}`;
    }
  }
  const parts = value.split(',').map((p) => p.trim());
  if (parts.length >= 3) {
    const nums = parts.slice(0, 3).map((p) => parseInt(p, 10));
    if (nums.every((n) => Number.isFinite(n))) {
      return nums.join(',');
    }
  }
  return value;
}

function normalizeType(raw) {
  if (raw == null || String(raw).trim() === '') {
    return DEFAULT_TYPE;
  }
  const v = String(raw).trim().toLowerCase();
  if (v === 'point' || v === '2') {
    return 2;
  }
  if (v === 'box' || v === '1') {
    return 1;
  }
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : DEFAULT_TYPE;
}

/**
 * Normalize a color string for set membership ("r,g,b").
 */
export function colorKey(color) {
  const normalized = normalizeColor(color);
  return normalized || null;
}

/**
 * Pick `count` colors from the default palette, preferring ones not in `usedColors`.
 * Within a batch, colors are unique while the unused pool lasts.
 */
export function pickRandomAvailableColors(count, usedColors = [], rng = Math.random) {
  const used = new Set();
  for (const c of usedColors || []) {
    const key = colorKey(c);
    if (key) {
      used.add(key);
    }
  }

  const result = [];
  for (let i = 0; i < count; i += 1) {
    const available = DEFAULT_LABEL_COLORS.filter((c) => !used.has(c));
    const pool = available.length > 0 ? available : DEFAULT_LABEL_COLORS;
    const idx = Math.floor(rng() * pool.length);
    const picked = pool[idx];
    result.push(picked);
    used.add(picked);
  }
  return result;
}

/**
 * Fill defaults for CSV rows: type=1, parent=null, random available color when missing.
 * @returns {{ name: string, color: string, type: number, parent: string|null }[]}
 */
export function applyImportDefaults(rows, existingLabels = [], rng = Math.random) {
  const used = [];
  for (const label of existingLabels || []) {
    if (label && label.color) {
      used.push(label.color);
    }
  }
  for (const row of rows || []) {
    if (row && row.color) {
      used.push(row.color);
    }
  }

  const needCount = (rows || []).filter((r) => !r || !r.color).length;
  const picked = pickRandomAvailableColors(needCount, used, rng);
  let pi = 0;

  return (rows || []).map((row) => ({
    name: row.name,
    type: row.type != null ? row.type : DEFAULT_TYPE,
    parent: row.parent || null,
    color: row.color || picked[pi++],
  }));
}

/**
 * Parse label rows from CSV text.
 * Missing color is left null — call applyImportDefaults before create.
 * @returns {{ name: string, color: string|null, type: number, parent: string|null }[]}
 */
export function parseLabelCsv(text) {
  const table = parseCsv(text);
  if (!table.length) {
    return [];
  }

  const first = table[0].map(normalizeHeader);
  const hasNameHeader = first.includes('name') || first[0] === 'name';
  let headerMap = null;
  let dataRows = table;

  if (hasNameHeader) {
    headerMap = {};
    first.forEach((h, idx) => {
      headerMap[h] = idx;
    });
    dataRows = table.slice(1);
  } else if (first.length === 1) {
    // Bare list of names, no header
    headerMap = { name: 0 };
  } else {
    // Assume first column is name
    headerMap = { name: 0 };
    if (first.length > 1) {
      headerMap.color = 1;
    }
    if (first.length > 2) {
      headerMap.type = 2;
    }
    if (first.length > 3) {
      headerMap.parent = 3;
    }
  }

  const nameIdx = headerMap.name != null ? headerMap.name : 0;
  const out = [];
  const seen = new Set();

  for (const row of dataRows) {
    const name = (row[nameIdx] || '').trim();
    if (!name) {
      continue;
    }
    const key = name.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);

    const colorRaw =
      headerMap.color != null && row[headerMap.color] != null
        ? row[headerMap.color]
        : '';
    const typeRaw =
      headerMap.type != null && row[headerMap.type] != null
        ? row[headerMap.type]
        : '';
    const parentRaw =
      headerMap.parent != null && row[headerMap.parent] != null
        ? row[headerMap.parent]
        : '';

    out.push({
      name,
      color: normalizeColor(colorRaw),
      type: normalizeType(typeRaw),
      parent: parentRaw && String(parentRaw).trim() ? String(parentRaw).trim() : null,
    });
  }
  return out;
}

/**
 * Build CSV text from Box21 label objects (store / API shape).
 * Uses parent name when a parent_id map is available.
 */
export function labelsToCsv(labels) {
  const byId = new Map();
  (labels || []).forEach((l) => {
    if (l && l.id != null) {
      byId.set(l.id, l);
    }
  });

  const rows = [['name', 'color', 'type', 'parent']];
  const sorted = [...(labels || [])].sort((a, b) =>
    String(a.name || '').localeCompare(String(b.name || '')),
  );

  for (const label of sorted) {
    const parent =
      label.parent_id != null && byId.has(label.parent_id)
        ? byId.get(label.parent_id).name
        : '';
    rows.push([
      label.name || '',
      label.color || '',
      label.type != null ? String(label.type) : '1',
      parent || '',
    ]);
  }
  return rowsToCsv(rows);
}

export function downloadTextFile(filename, content, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Split parsed CSV labels into existing vs missing (by case-insensitive name).
 */
export function partitionLabels(csvLabels, existingLabels) {
  const byName = new Map();
  (existingLabels || []).forEach((l) => {
    if (l && l.name) {
      byName.set(String(l.name).toLowerCase(), l);
    }
  });

  const existing = [];
  const missing = [];
  for (const row of csvLabels || []) {
    const found = byName.get(String(row.name).toLowerCase());
    if (found) {
      existing.push({ ...row, id: found.id, box21_label: found });
    } else {
      missing.push({ ...row });
    }
  }
  return { existing, missing };
}

/**
 * Create missing labels via the bulk label API (chunked), resolving parents when possible.
 * Falls back to per-label add when bulkAdd is unavailable.
 * Applies import defaults (type=1, parent=null, random available color) when missing.
 */
export async function createMissingLabels(apiLabel, missingRows, existingLabels, { maxPasses = 5, chunkSize = 2000 } = {}) {
  const nameToId = new Map();
  (existingLabels || []).forEach((l) => {
    if (l && l.name) {
      nameToId.set(String(l.name).toLowerCase(), l.id);
    }
  });

  const prepared = applyImportDefaults(missingRows || [], existingLabels || []);
  if (!prepared.length) {
    return { created: [], nameToId };
  }

  if (typeof apiLabel.bulkAdd === 'function') {
    const created = [];
    for (let i = 0; i < prepared.length; i += chunkSize) {
      const chunk = prepared.slice(i, i + chunkSize).map((row) => ({
        name: row.name,
        type: row.type || DEFAULT_TYPE,
        color: row.color || null,
        parent: row.parent || null,
      }));
      const response = await apiLabel.bulkAdd(chunk);
      const batch = (response && response.created) || [];
      for (const label of batch) {
        if (label && label.id) {
          nameToId.set(String(label.name).toLowerCase(), label.id);
          created.push(label);
        }
      }
    }
    return { created, nameToId };
  }

  const pending = [...prepared];
  const created = [];
  let pass = 0;

  const usedColorsSoFar = () =>
    [...(existingLabels || []), ...created]
      .map((l) => l && l.color)
      .filter(Boolean);

  while (pending.length && pass < maxPasses) {
    pass += 1;
    const stillPending = [];
    let createdThisPass = 0;

    for (const row of pending) {
      const parentName = row.parent;
      let parentId = null;
      if (parentName) {
        parentId = nameToId.get(String(parentName).toLowerCase()) || null;
        const parentAlsoMissing = pending.some(
          (p) => p !== row && String(p.name).toLowerCase() === String(parentName).toLowerCase(),
        );
        if (parentId == null && parentAlsoMissing) {
          stillPending.push(row);
          continue;
        }
      }

      const payload = {
        name: row.name,
        type: row.type || DEFAULT_TYPE,
        color: row.color || pickRandomAvailableColors(1, usedColorsSoFar())[0],
      };
      if (parentId != null) {
        payload.parent_id = parentId;
      }
      const response = await apiLabel.add(payload);
      if (response && response.id) {
        nameToId.set(String(row.name).toLowerCase(), response.id);
        created.push(response);
        createdThisPass += 1;
        if (parentId != null && response.parent_id !== parentId && apiLabel.patch) {
          await apiLabel.patch({
            id: response.id,
            name: response.name,
            color: response.color || payload.color,
            type: response.type || payload.type,
            parent_id: parentId,
          });
        }
      } else {
        stillPending.push(row);
      }
    }

    if (createdThisPass === 0 && stillPending.length === pending.length) {
      for (const row of stillPending.splice(0)) {
        const response = await apiLabel.add({
          name: row.name,
          type: row.type || DEFAULT_TYPE,
          color: row.color || pickRandomAvailableColors(1, usedColorsSoFar())[0],
        });
        if (response && response.id) {
          nameToId.set(String(row.name).toLowerCase(), response.id);
          created.push(response);
        }
      }
      break;
    }
    pending.length = 0;
    pending.push(...stillPending);
  }

  return { created, nameToId };
}

/**
 * Partition CSV rows against the server by looking up names in chunks
 * (avoids requiring the full label catalog in the Vuex store).
 */
export async function partitionLabelsViaApi(apiLabel, csvLabels, { chunkSize = 500 } = {}) {
  const existing = [];
  const missing = [];
  const rows = csvLabels || [];
  for (let i = 0; i < rows.length; i += chunkSize) {
    const chunk = rows.slice(i, i + chunkSize);
    const names = chunk.map((r) => r.name);
    const found = (await apiLabel.fetchByNames(names)) || [];
    const byName = new Map();
    for (const l of found) {
      if (l && l.name) {
        byName.set(String(l.name).toLowerCase(), l);
      }
    }
    for (const row of chunk) {
      const hit = byName.get(String(row.name).toLowerCase());
      if (hit) {
        existing.push({ ...row, id: hit.id, box21_label: hit });
      } else {
        missing.push({ ...row });
      }
    }
  }
  return { existing, missing };
}
